type Throttled<TArgs extends unknown[]> = ((...args: TArgs) => void) & {cancel: () => void}

/**
 * Call `fn` at most once per `waitMs`, on the leading edge and again on the trailing
 * edge when calls kept arriving.
 *
 * The board's auto-scroll needs this for one thing: recalculating its buffers when the
 * window resizes. The implementation this was lifted from used lodash, but a packaging
 * test pins `@github-ui/board-core` to `react` and the drag library, and nothing else.
 * Twenty lines is a cheaper price than a runtime dependency in a package other teams are
 * meant to adopt.
 */
export function throttle<TArgs extends unknown[]>(fn: (...args: TArgs) => void, waitMs: number): Throttled<TArgs> {
  let lastCallAt = 0
  let timer: ReturnType<typeof setTimeout> | undefined
  let pendingArgs: TArgs | undefined

  function invoke(args: TArgs) {
    lastCallAt = Date.now()
    fn(...args)
  }

  function throttled(...args: TArgs) {
    const elapsed = Date.now() - lastCallAt

    if (elapsed >= waitMs) {
      // Anything waiting on the trailing edge is now stale, and running it after this
      // call would deliver older arguments later. A timer that is due but has not been
      // reached, on a blocked main thread or a background tab, is exactly how that
      // happens.
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
      pendingArgs = undefined

      invoke(args)
      return
    }

    // Inside the window. Remember the newest arguments and let the timer already in
    // flight deliver them, so a burst of resize events produces one trailing call.
    pendingArgs = args
    if (timer !== undefined) return

    timer = setTimeout(() => {
      timer = undefined
      if (pendingArgs === undefined) return

      const trailing = pendingArgs
      pendingArgs = undefined
      invoke(trailing)
    }, waitMs - elapsed)
  }

  throttled.cancel = () => {
    if (timer !== undefined) clearTimeout(timer)
    timer = undefined
    pendingArgs = undefined
  }

  return throttled
}
